import React, { useEffect, useState } from "react";
import { useAdminAuth } from "../../lib/admin-auth-context";
import {
  ChatCircle,
  Users,
  Package,
  Receipt,
  Truck,
  CurrencyDollar,
  Warning,
} from "@phosphor-icons/react";

const Card = ({ icon: Icon, label, value, hint, color = "blue", testid }) => {
  const ring = {
    blue: "rgba(0,82,255,0.08)",
    amber: "rgba(245,158,11,0.10)",
    green: "rgba(16,185,129,0.10)",
    red: "rgba(220,38,38,0.10)",
  }[color];
  const textColor = {
    blue: "var(--icor-blue)",
    amber: "#b45309",
    green: "#047857",
    red: "#b91c1c",
  }[color];
  return (
    <div
      data-testid={testid}
      className="bg-white border border-slate-200 p-6"
      style={{ borderRadius: 4 }}
    >
      <div
        className="w-11 h-11 flex items-center justify-center mb-4"
        style={{ background: ring, color: textColor, borderRadius: 4 }}
      >
        <Icon size={22} weight="duotone" />
      </div>
      <div className="text-xs tracking-[0.18em] uppercase font-semibold text-slate-500">
        {label}
      </div>
      <div className="font-display text-3xl md:text-4xl font-bold tracking-tighter mt-1.5 text-[var(--icor-navy)]">
        {value}
      </div>
      {hint && <div className="text-xs text-slate-500 mt-2">{hint}</div>}
    </div>
  );
};

const fmtMoney = (n) =>
  `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}`;

export default function AdminDashboard() {
  const { api, user } = useAdminAuth();
  const [k, setK] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setK(r.data)).catch(() => setK({}));
  }, [api]);

  return (
    <div data-testid="admin-dashboard">
      <p className="text-xs tracking-[0.24em] uppercase font-bold text-[var(--icor-blue)] eyebrow-line">
        Vista general
      </p>
      <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tighter mt-3 text-[var(--icor-navy)]">
        Hola, {user?.name?.split(" ")[0]} 👋
      </h1>
      <p className="text-slate-500 mt-2 max-w-xl">
        Resumen operativo, financiero y logístico al día de hoy.
      </p>

      {!k ? (
        <div className="mt-10 text-slate-400">Cargando indicadores…</div>
      ) : (
        <>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card
              testid="kpi-quotes-month"
              icon={ChatCircle}
              label="Cotizaciones del mes"
              value={k.quotes_month ?? 0}
              hint={`${k.quotes_total ?? 0} históricas`}
              color="blue"
            />
            <Card
              testid="kpi-clients"
              icon={Users}
              label="Clientes activos"
              value={k.clients_total ?? 0}
              color="green"
            />
            <Card
              testid="kpi-orders-pending"
              icon={Receipt}
              label="Pedidos por surtir"
              value={k.orders_pending ?? 0}
              color="amber"
            />
            <Card
              testid="kpi-shipments"
              icon={Truck}
              label="Envíos en tránsito"
              value={k.shipments_in_transit ?? 0}
              color="blue"
            />
          </div>

          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card
              testid="kpi-revenue"
              icon={CurrencyDollar}
              label="Ingresos del mes"
              value={fmtMoney(k.revenue_month)}
              hint="Facturas pagadas"
              color="green"
            />
            <Card
              testid="kpi-outstanding"
              icon={CurrencyDollar}
              label="Por cobrar"
              value={fmtMoney(k.outstanding)}
              hint="Emitidas + vencidas"
              color="amber"
            />
            <Card
              testid="kpi-inventory"
              icon={Package}
              label="SKUs en catálogo"
              value={k.inventory_items ?? 0}
              color="blue"
            />
            <Card
              testid="kpi-low-stock"
              icon={Warning}
              label="Stock bajo"
              value={k.low_stock ?? 0}
              hint="Reordenar pronto"
              color={k.low_stock > 0 ? "red" : "green"}
            />
          </div>
        </>
      )}
    </div>
  );
}
